/**
 * 모달 폼의 입력 프리미티브.
 *
 * `controls.tsx`와 나눠 둔 이유: 그쪽은 **조작**(버튼·칩·점)이고 이쪽은 **입력**이다. 한 파일에 두면
 * 설정 화면만 쓰는 입력 스킨이 티켓 화면의 버튼 옆에 섞인다.
 */

const CONTROL =
  'w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-[12.5px] text-ink outline-none transition '
  + 'focus:border-accent disabled:cursor-not-allowed disabled:opacity-50';

/**
 * 라벨 + 컨트롤 한 쌍. 라벨이 컨트롤을 **감싼다** — `htmlFor`/`id` 짝을 호출부마다 만들지 않고도
 * 라벨 클릭·스크린리더 연결이 생긴다. 대신 한 `Field`는 컨트롤 **하나만** 담는다(하나의 `<label>`이
 * 둘을 감싸면 어느 쪽과 연결되는지가 브라우저 재량이 된다) — 두 개를 나란히 두려면 `FieldRow`로 감싼다.
 */
export function Field({
  label, hint, children,
}: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <label className="mt-3 block">
      <span className="mb-1 block text-[11px] font-semibold text-muted">{label}</span>
      {children}
      {hint !== undefined && <span className="mt-1 block text-[11px] leading-relaxed text-faint">{hint}</span>}
    </label>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  return <input {...props} className={`${CONTROL} ${props.className ?? ''}`} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>): React.JSX.Element {
  return <select {...props} className={`${CONTROL} cursor-pointer ${props.className ?? ''}`} />;
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>): React.JSX.Element {
  return (
    <textarea
      spellCheck={false}
      {...props}
      className={`${CONTROL} min-h-[84px] resize-y font-mono text-[11.5px] leading-relaxed ${props.className ?? ''}`}
    />
  );
}

/** 두 필드를 한 줄에. 좁은 화면에서는 자연히 위아래로 접힌다. */
export function FieldRow({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className="flex flex-col gap-x-3 sm:flex-row [&>*]:min-w-0 [&>*]:flex-1">{children}</div>;
}

/**
 * 모달 안에서 실패를 알리는 자리.
 *
 * 섹션 머리의 메시지 줄에 띄우면 **모달에 가려 안 보인다** — 저장을 눌렀는데 아무 일도 안 일어난 것처럼
 * 보이고, 사용자는 같은 버튼을 다시 누른다.
 */
export function FormError({ message }: { message: string }): React.JSX.Element {
  return (
    <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-2 text-[12px] leading-relaxed text-rose-700">
      {message}
    </p>
  );
}

/** 안내 문구 — 필드에 딸리지 않는 설명(모달 전체에 걸리는 규약 등). */
export function FormNote({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <p className="mt-3 rounded-md bg-line-soft px-2.5 py-2 text-[11px] leading-relaxed text-muted">{children}</p>
  );
}

/** 저장·취소 줄. 위험한 조작은 `ml-auto`로 오른쪽 끝에 밀어 저장 버튼과 손이 겹치지 않게 한다. */
export function FormActions({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-line pt-4">{children}</div>;
}
